/**
 * Broker Accounts page (Broker Integration — Phase 1: Broker Account
 * Management). Lets a user securely connect/reconnect/disconnect their
 * Dhan, Zerodha (Kite Connect), or ICICI Direct (Breeze) trading accounts,
 * and mark one as the default — nothing here places orders, reads
 * positions/holdings/funds, or does anything resembling live/paper trading
 * (see backend `app.services.broker_account_service`'s module docstring for
 * the full out-of-scope list for this phase).
 *
 * Always renders exactly 3 cards, one per `BROKER_ORDER` entry, regardless
 * of how many the user has actually connected — a broker with no
 * `broker_accounts` row yet still gets a card, just in the "not connected"
 * state (see `BrokerAccountCard`).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import brokerAccountService from "@/services/brokerAccountService";
import type { BrokerAccountRead, BrokerName } from "@/types";
import { extractErrorMessage } from "@/utils/errors";
import HelpGuideLink from "@/components/HelpGuideLink";
import { BROKER_META, BROKER_ORDER, BrokerAccountCard, ConnectDialog, DetailsDialog } from "@/pages/broker-accounts";
import { CALLBACK_BROKER_LABEL, callbackErrorMessage } from "./broker-accounts/callbackMessages";

export function BrokerAccountsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [accounts, setAccounts] = useState<BrokerAccountRead[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [connectTarget, setConnectTarget] = useState<{ broker: BrokerName; account?: BrokerAccountRead } | null>(
    null
  );
  const [detailsTarget, setDetailsTarget] = useState<{ broker: BrokerName; account: BrokerAccountRead } | null>(
    null
  );
  const [disconnectTarget, setDisconnectTarget] = useState<{ broker: BrokerName; account: BrokerAccountRead } | null>(
    null
  );
  const [disconnectBusy, setDisconnectBusy] = useState(false);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);
  const [defaultBusyId, setDefaultBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await brokerAccountService.list();
      setAccounts(response.items);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // After Zerodha's OAuth redirect lands back here (see backend
  // `app.api.v1.broker_accounts.zerodha_oauth_callback`), show the result as
  // a one-time notification, then strip the query params so a page refresh
  // doesn't re-show it.
  useEffect(() => {
    const connected = searchParams.get("connected");
    if (!connected) return;
    const errorCode = searchParams.get("error");
    // `connected` names the broker whose redirect landed here (zerodha /
    // angel_one); the messages for Zerodha are unchanged.
    const name = CALLBACK_BROKER_LABEL[connected] ?? "The broker";
    if (errorCode) {
      setError(callbackErrorMessage(name, errorCode));
    } else {
      setSuccessMessage(`${name} connected successfully.`);
      load();
    }
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const accountsByBroker = useMemo(() => {
    const map = new Map<BrokerName, BrokerAccountRead>();
    for (const account of accounts) {
      map.set(account.broker_name, account);
    }
    return map;
  }, [accounts]);

  const handleRefreshStatus = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const response = await brokerAccountService.refreshStatus();
      setAccounts(response.items);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setRefreshing(false);
    }
  };

  const handleConnected = (account: BrokerAccountRead) => {
    setAccounts((prev) => {
      const others = prev.filter((a) => a.id !== account.id);
      return [...others, account];
    });
    setConnectTarget(null);
    setSuccessMessage(`${BROKER_META[account.broker_name].displayName} connected successfully.`);
  };

  const confirmDisconnect = async () => {
    if (!disconnectTarget) return;
    setDisconnectBusy(true);
    setDisconnectError(null);
    try {
      const updated = await brokerAccountService.disconnect({ account_id: disconnectTarget.account.id });
      setAccounts((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
      setSuccessMessage(`${BROKER_META[disconnectTarget.broker].displayName} disconnected.`);
      setDisconnectTarget(null);
    } catch (err) {
      setDisconnectError(extractErrorMessage(err));
    } finally {
      setDisconnectBusy(false);
    }
  };

  const handleSetDefault = async (account: BrokerAccountRead) => {
    setDefaultBusyId(account.id);
    setError(null);
    try {
      const updated = await brokerAccountService.setDefault({ account_id: account.id });
      // The backend clears every OTHER row's is_default in the same
      // request — reflect that locally too instead of re-fetching, so the
      // UI stays snappy.
      setAccounts((prev) => prev.map((a) => (a.id === updated.id ? updated : { ...a, is_default: false })));
      setSuccessMessage(`${BROKER_META[account.broker_name].displayName} set as your default broker.`);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setDefaultBusyId(null);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} flexWrap="wrap" gap={2}>
        <Box>
          
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <HelpGuideLink section="broker-account" />
          <Tooltip title="Refresh connection status">
            <span>
              <IconButton onClick={handleRefreshStatus} disabled={loading || refreshing}>
                {refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {BROKER_ORDER.map((broker) => {
            const account = accountsByBroker.get(broker);
            return (
              <Grid item xs={12} sm={6} md={4} key={broker}>
                <BrokerAccountCard
                  broker={broker}
                  account={account}
                  defaultBusy={Boolean(account && defaultBusyId === account.id)}
                  onConnect={() => setConnectTarget({ broker })}
                  onReconnect={() => setConnectTarget({ broker, account })}
                  onDisconnect={() => account && setDisconnectTarget({ broker, account })}
                  onViewDetails={() => account && setDetailsTarget({ broker, account })}
                  onSetDefault={() => account && handleSetDefault(account)}
                />
              </Grid>
            );
          })}
        </Grid>
      )}

      <ConnectDialog
        open={Boolean(connectTarget)}
        broker={connectTarget?.broker ?? null}
        account={connectTarget?.account}
        onClose={() => setConnectTarget(null)}
        onConnected={handleConnected}
      />

      <DetailsDialog
        open={Boolean(detailsTarget)}
        broker={detailsTarget?.broker ?? null}
        account={detailsTarget?.account ?? null}
        onClose={() => setDetailsTarget(null)}
      />

      {/* ---- Disconnect confirmation ---- */}
      <Dialog open={Boolean(disconnectTarget)} onClose={() => (!disconnectBusy ? setDisconnectTarget(null) : undefined)}>
        <DialogTitle>
          Disconnect {disconnectTarget ? BROKER_META[disconnectTarget.broker].displayName : ""}?
        </DialogTitle>
        <DialogContent>
          {disconnectError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {disconnectError}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary">
            This ends the current session and removes stored access credentials. You can reconnect at any
            time — your Client ID and Account Name are kept.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisconnectTarget(null)} disabled={disconnectBusy}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDisconnect}
            disabled={disconnectBusy}
            startIcon={disconnectBusy ? <CircularProgress size={16} /> : undefined}
          >
            Disconnect
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ width: "100%" }}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Typography variant="caption" color="text.secondary" display="block" mt={4} sx={{ opacity: 0.8 }}>
        Broker account linking is provided for connectivity purposes only. TekInvest does not place orders,
        modify orders, or access positions, holdings or funds on your behalf in this phase.
      </Typography>
    </Box>
  );
}

export default BrokerAccountsPage;
